import { Typography } from '@mui/material';
import { List, ListType } from 'crono-fe-common/types/list';
import ListSearch from '../ListSearch';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { colors } from 'crono-fe-common/theme';
import IconBottomArrow from 'crono-fe-common/icons/Icon-Bottom-Arrow';
import IconTopArrow from 'crono-fe-common/icons/Icon-Top-Arrow';
import { ListDropdowntWrapper } from './style';
import IconSClose from 'crono-fe-common/icons/Icon-S-Close';
import { useListsContext } from 'context/lists';
import useGetList from 'hooks/services/lists/useGetList';

interface IProps {
  selectedList: List | null;
  setSelectedList:
    | Dispatch<SetStateAction<List | null>>
    | ((list: List | null) => void);
  listType: ListType;
  dropdownWidth?: string;
  inset: string;
  customEmptyText?: string;
  hideTriggerAndAutoOpen?: boolean;
  onChooseList?: () => void;
  wrapperHeight?: string;
  hideCrossAndIntroduceAllOption?: boolean;
  autoFillList?: boolean;
  defaultNewListName?: string;
  /** Height of the trigger: 40px (`md`, the default) or 32px (`sm`). */
  size?: 'sm' | 'md';
}

const ListDropdown = ({
  selectedList,
  setSelectedList,
  listType,
  dropdownWidth,
  inset,
  customEmptyText,
  hideTriggerAndAutoOpen = false,
  onChooseList,
  wrapperHeight,
  hideCrossAndIntroduceAllOption = false,
  autoFillList,
  defaultNewListName,
  size = 'md',
}: IProps) => {
  const [listDropdownOpened, setListDropdownOpened] = useState<boolean>(false);
  const { currentListId: listId } = useListsContext();

  const { data: listData } = useGetList(listId);
  const list = listData?.data?.data;

  const handleClearList = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      event.stopPropagation();
      setSelectedList(null);
    },
    [setSelectedList],
  );

  useEffect(() => {
    if (list && list.type === listType && autoFillList) {
      setSelectedList(list);
    }
  }, [list]);

  return (
    <ListDropdowntWrapper>
      {!hideTriggerAndAutoOpen && (
        <div
          className={`list-select${size === 'sm' ? ' list-select--sm' : ''}`}
          onClick={() => setListDropdownOpened((prev) => !prev)}
        >
          <Typography
            fontSize={14}
            lineHeight={'24px'}
            fontWeight={400}
            color={selectedList ? colors.black : colors.grey11}
            overflow={'hidden'}
            textOverflow={'ellipsis'}
            whiteSpace={'nowrap'}
          >
            {selectedList
              ? selectedList.name
              : customEmptyText
                ? customEmptyText
                : 'Select lists (optional)'}
          </Typography>

          <div className="list-select__left">
            {selectedList && !hideCrossAndIntroduceAllOption && (
              <div className="list-select__remove" onClick={handleClearList}>
                <IconSClose />
              </div>
            )}

            <div className={'list-select__arrow'}>
              {!listDropdownOpened ? <IconBottomArrow /> : <IconTopArrow />}
            </div>
          </div>
        </div>
      )}

      {(listDropdownOpened || hideTriggerAndAutoOpen) && (
        <div
          onKeyDown={(event) => {
            if (event.key !== 'Escape' || hideTriggerAndAutoOpen) return;
            // Handled here, so a dialog or drawer around it stays open.
            event.preventDefault();
            setListDropdownOpened(false);
          }}
          style={{
            position: 'absolute',
            inset: inset,
            zIndex: 10000,
            width: dropdownWidth ?? '100%',
          }}
        >
          <ListSearch
            listType={listType}
            onClose={() => setListDropdownOpened(false)}
            onChooseList={(list: List | null) => {
              setSelectedList(list);
              if (onChooseList) {
                onChooseList();
              }
            }}
            showAllOption={hideCrossAndIntroduceAllOption}
            forDropdown
            maxResultsHeight={'300px'}
            wrapperHeight={wrapperHeight}
            defaultNewListName={defaultNewListName}
          />
        </div>
      )}
    </ListDropdowntWrapper>
  );
};

export default ListDropdown;
